import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { crmApi } from "../api/client";
import { useOrgMode } from "./useOrgMode";

const LIST_KEY = ["crm", "companies"];

export function useCompanies({ enabled = true } = {}) {
  return useQuery({
    queryKey: LIST_KEY,
    enabled,
    queryFn: async () => {
      const { data } = await crmApi.get("/companies");
      return Array.isArray(data?.items) ? data.items : [];
    },
    staleTime: 60_000,
  });
}

function invalidateCompanies(qc) {
  qc.invalidateQueries({ queryKey: LIST_KEY });
  qc.invalidateQueries({ queryKey: ["crm", "org-mode"] });
  qc.invalidateQueries({ queryKey: ["crm", "projects"] });
  qc.invalidateQueries({ queryKey: ["crm", "leads"] });
}

export function useCreateCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload) => {
      const { data } = await crmApi.post("/companies", payload);
      return data?.company;
    },
    onSuccess: () => invalidateCompanies(qc),
  });
}

export function useUpdateCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ companyId, patch }) => {
      const { data } = await crmApi.patch(`/companies/${companyId}`, patch);
      return data?.company;
    },
    onSuccess: () => invalidateCompanies(qc),
  });
}

export function useArchiveCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (companyId) => {
      const { data } = await crmApi.post(`/companies/${companyId}/archive`);
      return data?.company;
    },
    onSuccess: () => invalidateCompanies(qc),
  });
}

export function useAssignProjectsToCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ companyId, projectIds }) => {
      const { data } = await crmApi.post(`/companies/${companyId}/assign-projects`, { projectIds });
      return data;
    },
    onSuccess: () => invalidateCompanies(qc),
  });
}

export function useAssignLeadsToCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ companyId, leadIds }) => {
      const { data } = await crmApi.post(`/companies/${companyId}/assign-leads`, { leadIds });
      return data;
    },
    onSuccess: () => invalidateCompanies(qc),
  });
}

// Unassigned projects for Settings → Companies bulk-assign.
export function useUnassignedProjects() {
  const { data: orgMode } = useOrgMode();
  const canAssign = !!orgMode?.usesCompanies || !!orgMode?.hasCompanies;
  return useQuery({
    queryKey: ["crm", "projects", "unassigned", { usesCompanies: !!orgMode?.usesCompanies }],
    enabled: canAssign,
    queryFn: async () => {
      if (!orgMode?.usesCompanies) {
        const { data } = await crmApi.get("/crm-projects", { params: { status: "all" } });
        const items = Array.isArray(data?.items) ? data.items : [];
        return items.filter((p) => !p.companyId);
      }
      const { data } = await crmApi.get("/crm-projects", { params: { unassigned: "true", status: "all" } });
      return Array.isArray(data?.items) ? data.items : [];
    },
    staleTime: 30_000,
  });
}

// Unassigned leads for Settings → Companies bulk-assign.
export function useUnassignedLeads() {
  const { data: orgMode } = useOrgMode();
  const canAssign = !!orgMode?.usesCompanies || !!orgMode?.hasCompanies;
  return useQuery({
    queryKey: ["crm", "leads", "unassigned", { usesCompanies: !!orgMode?.usesCompanies }],
    enabled: canAssign && !!orgMode?.usesCompanies,
    queryFn: async () => {
      const { data } = await crmApi.get("/leads", { params: { unassigned: "true", limit: 200, skip: 0 } });
      return Array.isArray(data?.items) ? data.items : [];
    },
    staleTime: 30_000,
  });
}
